/**
 * Capacitor Camera Plugin interfaces based on @capacitor/camera
 * See CAMERA_API.md for full documentation
 */

/**
 * How the photo data should be returned.
 */
export enum CameraResultType {
    /** Return the URI of the image (file:// on native, blob URL on web) */
    Uri = 'uri',
    /** Return the base64-encoded data of the image */
    Base64 = 'base64',
    /** Return the data URL (base64 with MIME type prefix) */
    DataUrl = 'dataUrl'
}

/**
 * The source of the photo.
 */
export enum CameraSource {
    /** Prompt the user to select either the camera or photo library */
    Prompt = 'PROMPT',
    /** Use the device camera */
    Camera = 'CAMERA',
    /** Use the device photo library */
    Photos = 'PHOTOS'
}

/**
 * Which camera to use (iOS and Web only).
 */
export enum CameraDirection {
    /** Use the rear-facing camera */
    Rear = 'REAR',
    /** Use the front-facing camera */
    Front = 'FRONT'
}

/**
 * Camera permission state.
 */
export type CameraPermissionState = 'prompt' | 'granted' | 'denied' | 'limited';

/**
 * Result of Camera permission check.
 */
export interface CameraPermissionStatus {
    camera: CameraPermissionState;
}

/**
 * Permission state values.
 */
export type PermissionState = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' | 'limited';

/**
 * Options for taking a photo or selecting from gallery.
 */
export interface ImageOptions {
    /** The quality of image to return (0-100). Default: 50 */
    quality?: number;
    /** Whether to allow the user to crop or make small edits. Default: false */
    allowEditing?: boolean;
    /** Required. How the data should be returned. */
    resultType: CameraResultType;
    /** Whether to save the photo to the gallery. Default: false */
    saveToGallery?: boolean;
    /** The desired maximum width of the saved image. */
    width?: number;
    /** The desired maximum height of the saved image. */
    height?: number;
    /** Whether to automatically rotate the image. Default: true */
    correctOrientation?: boolean;
    /** The source of the photo. Default: CameraSource.Prompt */
    source?: CameraSource;
    /** iOS/Web only. The default camera direction. Default: CameraDirection.Rear */
    direction?: CameraDirection;
    /** iOS only. Presentation style of the Camera. Default: 'fullscreen' */
    presentationStyle?: 'fullscreen' | 'popover';
    /** Web only. Whether to use input element instead of native picker. Default: false */
    webUseInput?: boolean;
    /** Text to display in the action sheet header. Default: 'Photo' */
    promptLabelHeader?: string;
    /** Text for the cancel button. Default: 'Cancel' */
    promptLabelCancel?: string;
    /** Text for the photo library button. Default: 'From Photos' */
    promptLabelPhoto?: string;
    /** Text for the take picture button. Default: 'Take Picture' */
    promptLabelPicture?: string;
}

/**
 * The result returned from getPhoto().
 */
export interface Photo {
    /** The base64 encoded string (if resultType was Base64) */
    base64String?: string;
    /** The data URL (if resultType was DataUrl) */
    dataUrl?: string;
    /** The path to the photo file on the device (native only) */
    path?: string;
    /** A web-accessible URL that can be used in an <img> tag */
    webPath?: string;
    /** EXIF data, if available */
    exif?: any;
    /** The format of the image (e.g., 'jpeg', 'png') */
    format: string;
    /** Whether the image was saved to the gallery */
    saved: boolean;
}

/**
 * Options for picking multiple images from the gallery.
 */
export interface GalleryImageOptions {
    /** The quality of images to return (0-100). Default: 90 */
    quality?: number;
    /** The desired maximum width of the saved images */
    width?: number;
    /** The desired maximum height of the saved images */
    height?: number;
    /** Whether to automatically rotate images. Default: true */
    correctOrientation?: boolean;
    /** iOS only. Presentation style. Default: 'fullscreen' */
    presentationStyle?: 'fullscreen' | 'popover';
    /** Maximum number of pictures to pick. 0 means unlimited. Default: 0 */
    limit?: number;
}

/**
 * A photo from the gallery picker.
 */
export interface GalleryPhoto {
    /** The path to the photo file on the device (native only) */
    path?: string;
    /** A web-accessible URL for use in <img> tags */
    webPath: string;
    /** EXIF data, if available */
    exif?: any;
    /** The format of the image */
    format: string;
}

/**
 * The result returned from pickImages().
 */
export interface GalleryPhotos {
    /** Array of selected photos */
    photos: GalleryPhoto[];
}

/**
 * Permission status for camera and photos.
 */
export interface PermissionStatus {
    /** Permission state for the camera */
    camera: PermissionState;
    /** Permission state for the photo library */
    photos: PermissionState;
}

/**
 * Which permissions to request.
 */
export interface CameraPluginPermissions {
    permissions: ('camera' | 'photos')[];
}

/**
 * Capacitor Camera Plugin interface.
 */
export interface MobileDeviceCameraDef {
    /**
     * Prompts the user to pick a photo from an album, or take a new photo with the camera.
     */
    getPhoto(options: ImageOptions): Promise<Photo>;

    /**
     * Allows the user to pick multiple pictures from the photo gallery.
     */
    pickImages(options: GalleryImageOptions): Promise<GalleryPhotos>;

    /**
     * iOS 14+ only. Allows the user to update their limited library selection.
     */
    pickLimitedLibraryPhotos(): Promise<GalleryPhotos>;

    /**
     * iOS 14+ only. Returns the photos the user has selected for limited access.
     */
    getLimitedLibraryPhotos(): Promise<GalleryPhotos>;

    /**
     * Check camera and photo album permissions.
     */
    checkPermissions(): Promise<PermissionStatus>;

    /**
     * Request camera and/or photo album permissions.
     */
    requestPermissions(permissions?: CameraPluginPermissions): Promise<PermissionStatus>;
}

